/**
 * AuthContext (R1.2a / R1.2a-FIX)
 *
 * - accessToken: in-memory for http client + persisted (sessionStorage on web, SecureStorage on native)
 * - refreshToken: persisted only (sessionStorage on web, SecureStorage on native)
 * - On reload: restore from access token if still valid; else refresh once; logout on refresh failure
 * - Logout: clears both tokens from storage and memory
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { Role } from '../api/types';
import { authApi } from '../api/auth';
import {
  registerHttpCallbacks,
  setAccessToken,
} from '../api/http';
import { SecureStorage } from '../platform/secureStorage';
import { consumeNewTabSessionHandoff } from '../platform/newTabSessionHandoff';

const REFRESH_TOKEN_KEY = 'cantir.refreshToken';
const ACCESS_TOKEN_KEY = 'cantir.accessToken';
const TENANT_ID_KEY = 'cantir.tenantId';

/** Consider access token expired this many ms before actual exp (clock skew). */
const EXP_BUFFER_MS = 10_000;

// ── Auth state ────────────────────────────────────────────────────────────────

export interface AuthUser {
  userId: string;
  tenantId: string;
  role: Role;
}

export interface TenantOption {
  tenantId: string;
  role: string;
  displayName: string;
}

/** R0.3: Billing snapshot carried in auth state (NOT in JWT). */
export interface BillingSnapshot {
  billingStatus: string;
  trialEndDate?: string | null;
  subscriptionEndDate?: string | null;
  /** Feature codes enabled for this tenant (e.g. FINANCE_ALERTS). */
  enabledFeatures?: string[];
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  billingSnapshot: BillingSnapshot | null;
  /** Valorizzato dopo login quando il backend restituisce TENANT_SELECTION_REQUIRED */
  pendingTenantSelection: { challengeId: string; tenants: TenantOption[] } | null;
  /** R0.4: when true, user must go to /app/change-password before accessing other routes */
  mustChangePassword: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<'SUCCESS' | 'TENANT_SELECTION_REQUIRED'>;
  chooseTenant: (challengeId: string, tenantId: string) => Promise<void>;
  clearPendingTenantSelection: () => void;
  logout: () => Promise<void>;
  refreshOnce: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseUserFromToken(token: string): AuthUser {
  const payload = JSON.parse(atob(token.split('.')[1]));
  if (!payload.user_id || !payload.tenant_id || !payload.role) {
    throw new Error('Invalid JWT claims: missing required fields (user_id, tenant_id, role)');
  }
  return {
    userId: payload.user_id,
    tenantId: payload.tenant_id,
    role: payload.role as Role,
  };
}

function isAccessTokenValid(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp as number | undefined;
    if (typeof exp !== 'number') return false;
    return exp * 1000 - EXP_BUFFER_MS > Date.now();
  } catch {
    return false;
  }
}

function extractBillingSnapshot(res: {
  billingStatus?: string;
  trialEndDate?: string | null;
  subscriptionEndDate?: string | null;
  enabledFeatures?: string[];
}): BillingSnapshot | null {
  if (!res.billingStatus) return null;
  return {
    billingStatus: res.billingStatus,
    trialEndDate: res.trialEndDate,
    subscriptionEndDate: res.subscriptionEndDate,
    enabledFeatures: res.enabledFeatures ?? [],
  };
}

// ── Provider ──────────────────────────────────────────────────────────────────

async function setSessionFromSuccess(
  res: {
    accessToken: string;
    refreshToken: string;
    tenantId: string;
    userId: string;
    role: string;
    billingStatus?: string;
    trialEndDate?: string | null;
    subscriptionEndDate?: string | null;
    enabledFeatures?: string[];
    mustChangePassword?: boolean;
  },
  setState: React.Dispatch<React.SetStateAction<AuthState>>,
) {
  setAccessToken(res.accessToken);
  await SecureStorage.set(ACCESS_TOKEN_KEY, res.accessToken);
  await SecureStorage.set(REFRESH_TOKEN_KEY, res.refreshToken);
  await SecureStorage.set(TENANT_ID_KEY, res.tenantId);
  setState({
    user: { userId: res.userId, tenantId: res.tenantId, role: res.role as Role },
    isLoading: false,
    billingSnapshot: extractBillingSnapshot(res),
    pendingTenantSelection: null,
    mustChangePassword: res.mustChangePassword === true,
  });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    billingSnapshot: null,
    pendingTenantSelection: null,
    mustChangePassword: false,
  });
  const logoutRef = useRef<() => void>(() => {});

  const logout = useCallback(async () => {
    const refreshToken = await SecureStorage.get(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      try {
        await authApi.logout({ refreshToken });
      } catch {
        // best-effort; clear locally regardless
      }
    }
    setAccessToken(null);
    await SecureStorage.remove(ACCESS_TOKEN_KEY);
    await SecureStorage.remove(REFRESH_TOKEN_KEY);
    await SecureStorage.remove(TENANT_ID_KEY);
    setState({ user: null, isLoading: false, billingSnapshot: null, pendingTenantSelection: null, mustChangePassword: false });
  }, []);

  logoutRef.current = logout;

  const refreshOnce = useCallback(async (): Promise<string> => {
    const refreshToken = await SecureStorage.get(REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new Error('No refresh token');

    const res = await authApi.refresh({ refreshToken });
    setAccessToken(res.accessToken);
    await SecureStorage.set(ACCESS_TOKEN_KEY, res.accessToken);
    await SecureStorage.set(REFRESH_TOKEN_KEY, res.refreshToken);

    // Update billing snapshot and mustChangePassword from refresh response (R0.3 / R0.4)
    setState((prev) => ({
      ...prev,
      billingSnapshot: extractBillingSnapshot(res) ?? prev.billingSnapshot,
      mustChangePassword: res.mustChangePassword === true,
    }));

    return res.accessToken;
  }, []);

  useEffect(() => {
    registerHttpCallbacks(
      refreshOnce,
      () => logoutRef.current(),
    );
  }, [refreshOnce]);

  // Restore session on mount: try access token first, then always refresh to get billingSnapshot (enabledFeatures)
  useEffect(() => {
    // Web: nuova scheda (nuovo sessionStorage) — ripristina token da handoff prima di SecureStorage.get
    consumeNewTabSessionHandoff();
    (async () => {
      const accessToken = await SecureStorage.get(ACCESS_TOKEN_KEY);
      const refreshToken = await SecureStorage.get(REFRESH_TOKEN_KEY);

      if (accessToken && isAccessTokenValid(accessToken)) {
        setAccessToken(accessToken);
        setState({
          user: parseUserFromToken(accessToken),
          isLoading: false,
          billingSnapshot: null,
          pendingTenantSelection: null,
          mustChangePassword: false,
        });
        // billingSnapshot (enabledFeatures) is not in JWT — fetch via refresh so menus show correctly
        if (refreshToken) {
          try {
            const res = await authApi.refresh({ refreshToken });
            setAccessToken(res.accessToken);
            await SecureStorage.set(ACCESS_TOKEN_KEY, res.accessToken);
            await SecureStorage.set(REFRESH_TOKEN_KEY, res.refreshToken);
            setState((prev) => ({
              ...prev,
              billingSnapshot: extractBillingSnapshot(res),
              mustChangePassword: res.mustChangePassword === true,
            }));
          } catch {
            // keep current session; billingSnapshot stays null until next refresh
          }
        }
        return;
      }

      if (!refreshToken) {
        setState({ user: null, isLoading: false, billingSnapshot: null, pendingTenantSelection: null, mustChangePassword: false });
        return;
      }
      try {
        const res = await authApi.refresh({ refreshToken });
        setAccessToken(res.accessToken);
        await SecureStorage.set(ACCESS_TOKEN_KEY, res.accessToken);
        await SecureStorage.set(REFRESH_TOKEN_KEY, res.refreshToken);
        setState({
          user: parseUserFromToken(res.accessToken),
          isLoading: false,
          billingSnapshot: extractBillingSnapshot(res),
          pendingTenantSelection: null,
          mustChangePassword: res.mustChangePassword === true,
        });
      } catch {
        await SecureStorage.remove(ACCESS_TOKEN_KEY);
        await SecureStorage.remove(REFRESH_TOKEN_KEY);
        setState({ user: null, isLoading: false, billingSnapshot: null, pendingTenantSelection: null, mustChangePassword: false });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email: string, password: string): Promise<'SUCCESS' | 'TENANT_SELECTION_REQUIRED'> => {
    const res = await authApi.login({ email, password });
    if (res.type === 'SUCCESS' && res.accessToken && res.refreshToken && res.tenantId && res.userId && res.role) {
      await setSessionFromSuccess(
        {
          accessToken: res.accessToken,
          refreshToken: res.refreshToken,
          tenantId: res.tenantId,
          userId: res.userId,
          role: res.role,
          billingStatus: res.billingStatus,
          trialEndDate: res.trialEndDate,
          subscriptionEndDate: res.subscriptionEndDate,
          enabledFeatures: res.enabledFeatures,
          mustChangePassword: res.mustChangePassword,
        },
        setState,
      );
      return 'SUCCESS';
    }
    if (res.type === 'TENANT_SELECTION_REQUIRED' && res.challengeId && res.tenants?.length) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        pendingTenantSelection: { challengeId: res.challengeId!, tenants: res.tenants! },
      }));
      return 'TENANT_SELECTION_REQUIRED';
    }
    throw new Error('Risposta di login non valida');
  }, []);

  const chooseTenant = useCallback(async (challengeId: string, tenantId: string) => {
    const res = await authApi.chooseTenant({ challengeId, tenantId });
    if (res.type === 'SUCCESS' && res.accessToken && res.refreshToken && res.tenantId && res.userId && res.role) {
      await setSessionFromSuccess(
        {
          accessToken: res.accessToken,
          refreshToken: res.refreshToken,
          tenantId: res.tenantId,
          userId: res.userId,
          role: res.role,
          billingStatus: res.billingStatus,
          trialEndDate: res.trialEndDate,
          subscriptionEndDate: res.subscriptionEndDate,
          enabledFeatures: res.enabledFeatures,
          mustChangePassword: res.mustChangePassword,
        },
        setState,
      );
      return;
    }
    throw new Error('Risposta choose-tenant non valida');
  }, []);

  const clearPendingTenantSelection = useCallback(() => {
    setState((prev) => ({ ...prev, pendingTenantSelection: null }));
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, chooseTenant, clearPendingTenantSelection, logout, refreshOnce }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
